import { collection, doc, getDoc, getDocs, increment, runTransaction, serverTimestamp, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { randomCode, FRIEND_CODE_LENGTH, pick } from "./ids";
import { maskBadWords } from "./text";
import { tt } from "../i18n";

export const AVATAR_EMOJIS = ["🦊", "🐸", "🐙", "🦄", "🐼", "🐯", "🐵", "🐲", "👾", "🤖", "👻", "🐧", "🦖", "🐝", "🍕", "🌮", "🍩", "🎃", "🦉", "🐨"];
export const AVATAR_COLORS = ["#ff6b9d", "#ffb84d", "#ffe156", "#6bdc8a", "#4dd4ff", "#7c5cff", "#c86bff", "#ff7b54"];

export const randomAvatar = () => ({ emoji: pick(AVATAR_EMOJIS), color: pick(AVATAR_COLORS) });

export const cleanName = (name) => maskBadWords((name || "").replace(/\s+/g, " ").trim().slice(0, 20));

// Creates the user's profile on first sign-in and reserves a unique friend
// code for them. The friendCodes/{code} doc guarantees uniqueness.
export async function ensureProfile(user, fallbackName) {
  const userRef = doc(db, "users", user.uid);
  const existing = await getDoc(userRef);
  if (existing.exists()) return existing.data();

  const displayName =
    cleanName(user.displayName) || cleanName(fallbackName) || cleanName(user.email?.split("@")[0]) || "Player";

  for (let attempt = 0; attempt < 8; attempt++) {
    const code = randomCode(FRIEND_CODE_LENGTH);
    const codeRef = doc(db, "friendCodes", code);
    try {
      const profile = await runTransaction(db, async (tx) => {
        const [codeSnap, userSnap] = await Promise.all([tx.get(codeRef), tx.get(userRef)]);
        if (userSnap.exists()) return userSnap.data();
        if (codeSnap.exists()) return null;
        const data = {
          uid: user.uid,
          displayName,
          avatar: randomAvatar(),
          friendCode: code,
          xp: 0,
          lastXpAt: serverTimestamp(),
          lastSeen: serverTimestamp(),
          createdAt: serverTimestamp(),
          status: null,
        };
        tx.set(codeRef, { uid: user.uid });
        tx.set(userRef, data);
        return data;
      });
      if (profile) return profile;
    } catch (e) {
      if (attempt === 7) throw e;
    }
  }
  throw new Error(tt("Couldn't reserve a friend code."));
}

export const updateProfileFields = (uid, fields) => updateDoc(doc(db, "users", uid), fields);

// Heartbeat: keeps "online" status fresh and awards time-based XP.
export function heartbeat(uid, xpGain, status) {
  const fields = { lastSeen: serverTimestamp(), status: status || null };
  if (xpGain > 0) {
    fields.xp = increment(xpGain);
    fields.lastXpAt = serverTimestamp();
  }
  return updateDoc(doc(db, "users", uid), fields);
}

// ---- Your data (GDPR) -------------------------------------------------------
const LISTS = ["friends", "requests", "invites"];

// Firestore timestamps become readable dates in the export.
const plain = (v) =>
  v?.toDate ? v.toDate().toISOString() : v && typeof v === "object" ? Object.fromEntries(Object.entries(v).map(([k, x]) => [k, plain(x)])) : v;

// Everything we store about an account, as one JSON-friendly object.
export async function exportMyData(user) {
  const [profile, ...lists] = await Promise.all([
    getDoc(doc(db, "users", user.uid)),
    ...LISTS.map((c) => getDocs(collection(db, "users", user.uid, c))),
  ]);
  const out = {
    exportedAt: new Date().toISOString(),
    account: { id: user.uid, email: user.email || null, signInMethods: user.providers || [] },
    profile: plain(profile.data() || null),
  };
  LISTS.forEach((c, i) => {
    out[c] = lists[i].docs.map((d) => ({ id: d.id, ...plain(d.data()) }));
  });
  return out;
}

// Deleting an account: removes friends (on both sides), friend requests and
// invites, and wipes the name and photo. What's left (a random ID, friend
// code and XP number) can't be linked to anyone once the login is deleted.
export async function eraseProfile(uid) {
  const lists = await Promise.all(LISTS.map((c) => getDocs(collection(db, "users", uid, c))));
  const refs = [];
  lists[0].forEach((f) => refs.push(f.ref, doc(db, "users", f.id, "friends", uid)));
  lists[1].forEach((r) => refs.push(r.ref));
  lists[2].forEach((i) => refs.push(i.ref));
  for (let i = 0; i < refs.length; i += 400) {
    const batch = writeBatch(db);
    refs.slice(i, i + 400).forEach((r) => batch.delete(r));
    await batch.commit();
  }
  await updateDoc(doc(db, "users", uid), {
    displayName: "Deleted player",
    avatar: { emoji: "👻", color: "#cccccc" },
    status: null,
  });
}
